import { useState } from "react";
import { useMrrTrend, type MrrDataPoint } from "@/hooks/useTenantBilling";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);

const PERIOD_OPTIONS = [
  { label: "30d", value: 30 },
  { label: "60d", value: 60 },
  { label: "90d", value: 90 },
] as const;

export const MrrTrendPanel = () => {
  const [days, setDays] = useState<number>(30);
  const [open, setOpen] = useState(true);
  const { trendData, netNewMrr, churnedMrr, expansionMrr, currentMrr, isLoading } =
    useMrrTrend(days);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <TrendingUp className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Revenue Trend</CardTitle>
                {open ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </CollapsibleTrigger>
            <div className="flex gap-1">
              {PERIOD_OPTIONS.map((p) => (
                <Button
                  key={p.value}
                  size="sm"
                  variant={days === p.value ? "default" : "outline"}
                  className="h-7 px-3 text-xs"
                  onClick={() => setDays(p.value)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-2 space-y-4">
            {isLoading ? (
              <div className="h-40 flex items-center justify-center">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : trendData.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">
                No billing data available yet.
              </p>
            ) : (
              <>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" hide />
                      <YAxis hide domain={["dataMin - 100", "dataMax + 100"]} />
                      <Tooltip
                        formatter={(value: number) => [fmt(value), "MRR"]}
                        labelFormatter={(label) => label}
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="mrr"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        fill="url(#mrrFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Stat chips */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs gap-1 py-1 px-2.5">
                    <span className="text-green-600 font-semibold">+{fmt(netNewMrr)}</span>
                    <span className="text-muted-foreground">Net New</span>
                  </Badge>
                  <Badge variant="outline" className="text-xs gap-1 py-1 px-2.5">
                    <span className="text-red-600 font-semibold">-{fmt(churnedMrr)}</span>
                    <span className="text-muted-foreground">Churned</span>
                  </Badge>
                  <Badge variant="outline" className="text-xs gap-1 py-1 px-2.5">
                    <span className="text-primary font-semibold">+{fmt(expansionMrr)}</span>
                    <span className="text-muted-foreground">Expansion</span>
                  </Badge>
                </div>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
